import { CartLineT } from '@lambda/apis/src/cart/types';
import { ToBeRemovedT } from './types';

// Identifies the specific delta shown for a line. Dismissing stores this
// signature so that a follow-up delta with different `stock`, `newPrice`,
// `newVendor`, or `removed` produces a fresh badge — matching the ticket's
// "resets and shows again once" rule.
export const dismissSignature = (item: ToBeRemovedT): string =>
  [
    item.line.typeId,
    item.stock,
    item.newPrice ?? '',
    item.newVendor ?? '',
    item.removed ? '1' : '0',
  ].join('|');

// A delta is worth surfacing only if the user's cart is actually affected.
// MsCart flags every stock change as a delta — including pure increases —
// which we ignore because the customer has nothing to act on.
const isNotableDelta = (item: ToBeRemovedT): boolean =>
  item.removed === true ||
  item.stock === 0 ||
  item.line.quantity > item.stock ||
  Boolean(
    item.priceChanged && item.oldPrice != null && item.newPrice != null,
  ) ||
  Boolean(item.vendorChanged && item.newVendor);

export const computeRemovedCartLines = ({
  toBeRemoved,
  reviewLines,
  cartLines,
  dismissed,
}: {
  toBeRemoved: ToBeRemovedT[];
  reviewLines: ToBeRemovedT[];
  cartLines: CartLineT[];
  dismissed: ReadonlySet<string>;
}): ToBeRemovedT[] => {
  const reviewTypeIds = new Set(reviewLines.map((l) => l.line.typeId));
  const cartLineIds = new Set(cartLines.map((l) => l.id));

  const merged = [
    ...toBeRemoved.filter((l) => !reviewTypeIds.has(l.line.typeId)),
    ...reviewLines,
  ];

  return merged.filter((item) => {
    if (!isNotableDelta(item)) {
      return false;
    }

    if (dismissed.has(dismissSignature(item))) {
      return false;
    }

    // OOS entries are removed from cart.lines by MsCart during revalidation
    // but must remain visible until the user acts on them.
    if (item.removed || item.stock === 0) {
      return true;
    }

    return cartLineIds.has(item.line.id);
  });
};
